import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

type Rules = () => ReactNode
type Registry = { rules: Rules | null; register: (rules: Rules) => () => void }
const Context = createContext<Registry | null>(null)

export function CoachScreenRules({ children }: { children: ReactNode }) {
  const [rules, setRules] = useState<Rules | null>(null)
  const register = useCallback((next: Rules) => {
    setRules(() => next)
    return () => setRules(current => (current === next ? null : current))
  }, [])
  const value = useMemo(() => ({ rules, register }), [rules, register])
  return <Context.Provider value={value}>{children}</Context.Provider>
}

// Screens supply their existing local content, including their current game variant.
export function useCoachScreenRules(render: Rules | undefined) {
  const register = useContext(Context)?.register
  const latest = useRef(render)
  useLayoutEffect(() => {
    latest.current = render
  })
  const enabled = !!render
  useLayoutEffect(() => {
    if (enabled && register) return register(() => latest.current?.())
  }, [enabled, register])
}

export function useCurrentCoachRules() {
  return useContext(Context)?.rules
}
